import { useMemo, useState } from 'react';
import { useSuspenseQuery } from '@tanstack/react-query';
import { Box } from '@givebutter/primitives/box';
import { Button } from '@givebutter/primitives/button';
import { LineChart } from '@givebutter/primitives/chart';
import { maskWholeNumber } from '@givebutter/util';
import { faCalendarDay, faCalendarDays } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { fetchWorkflowUsage } from 'api/workflows';
import { EmptyData } from 'components/util';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentWorkflow } from 'customHooks/useCurrentWorkflow';
import { transformCurrencyToCompactNumberFormat } from 'helpers/formatting';
import { DateTime, Interval } from 'luxon';
import { workflowQueryKeys } from 'queries/workflow-query-keys';

const WorkflowDetailsTriggerVolumeChart = () => {
    const [chartInterval, setChartInterval] = useState(DateTime.now().month > 2 ? 'month' : 'day');
    const { accountId } = useCurrentAccount();
    const { data: workflow } = useCurrentWorkflow();
    const { data: usage } = useSuspenseQuery({
        queryKey: workflowQueryKeys.workflowUsageById(accountId, workflow.id),
        queryFn: () => fetchWorkflowUsage(accountId, workflow.id),
    });

    const {
        triggered_workflows,
        current_month_triggered_workflows,
        last_period_triggered_workflows_percentage,
    } = usage;

    const data = useMemo(() => {
        const today = DateTime.now();
        const startOfYear = today.startOf('year');
        const interval = Interval.fromDateTimes(startOfYear, today);

        switch (chartInterval) {
            case 'day': {
                const arrayOfDays = interval.splitBy({ day: 1 }).map(d => d.start);
                const entriesMap = new Map(
                    usage.triggered_workflows_by_date.map(value => [value.date, value.count]),
                );

                return arrayOfDays.map(day => {
                    return {
                        Triggers: entriesMap.get(day.toISODate()) ?? 0,
                        date: day.toLocaleString(DateTime.DATE_MED),
                    };
                });
            }
            case 'month': {
                const arrayOfMonths = interval.splitBy({ month: 1 }).map(d => d.start);

                return arrayOfMonths.map(month => {
                    const monthShort = month.monthShort;

                    const triggerSumByMonth = usage.triggered_workflows_by_date.reduce(
                        (prev, curr) => {
                            const increment =
                                monthShort === DateTime.fromISO(curr.date).monthShort
                                    ? curr.count
                                    : 0;

                            return prev + increment;
                        },
                        0,
                    );

                    return {
                        Triggers: triggerSumByMonth,
                        date: month.monthShort,
                    };
                });
            }
            default:
                return [];
        }
    }, [usage, chartInterval]);

    return (
        <Box className="flex flex-col gap-3 p-6 py-4">
            <LineChart.Root
                allowDecimals={false}
                index={'date'}
                categories={['Triggers']}
                data={data}
                yAxisWidth={32}
                valueFormatter={(value: number) => {
                    if (value >= 1000000) {
                        return transformCurrencyToCompactNumberFormat(value);
                    }

                    return maskWholeNumber(value);
                }}
                tickFormatter={(formattedValue, rawValue) => {
                    if (rawValue > 999) {
                        return transformCurrencyToCompactNumberFormat(rawValue);
                    }

                    return formattedValue;
                }}
            >
                <div className="flex items-start justify-between">
                    <div className="flex flex-col gap-2">
                        <p className="text-sm">Year to date trigger volume</p>
                        <div className="flex gap-8">
                            <div className="flex items-center gap-1.5">
                                <p className="text-2xl font-medium">
                                    {maskWholeNumber(triggered_workflows)}
                                </p>
                                <div className="flex flex-col">
                                    <p className="text-xs font-medium text-gray-600">
                                        Total triggers
                                    </p>
                                    <p className="text-xs text-gray-600">this year</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-1.5">
                                <p className="text-2xl font-medium">
                                    {maskWholeNumber(current_month_triggered_workflows)}
                                </p>
                                <div className="flex flex-col">
                                    <p className="text-xs font-medium text-gray-600">
                                        Triggers this month
                                    </p>
                                    <p className="text-xs text-gray-600">
                                        <TriggerPeriodPercentage
                                            percentage={last_period_triggered_workflows_percentage}
                                        />{' '}
                                        vs last month
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className={'flex items-center gap-4'}>
                        <LineChart.Legend />
                        <Button
                            variant="outline"
                            color="neutral"
                            size="sm"
                            onClick={() => {
                                setChartInterval(interval =>
                                    interval === 'day' ? 'month' : 'day',
                                );
                            }}
                        >
                            <FontAwesomeIcon
                                icon={chartInterval === 'day' ? faCalendarDay : faCalendarDays}
                            />
                            {chartInterval === 'day' ? 'Daily' : 'Monthly'}
                        </Button>
                    </div>
                </div>
                <LineChart.Content className={'h-48'}>
                    <LineChart.Tooltip />
                </LineChart.Content>
            </LineChart.Root>
        </Box>
    );
};

const TriggerPeriodPercentage = ({ percentage }: { percentage: number }) => {
    if (percentage < 0) {
        return <span className="text-red-500">{percentage}%</span>;
    }

    if (percentage > 0) {
        return <span className="text-green-500">+{percentage}%</span>;
    }

    return <EmptyData />;
};

export { WorkflowDetailsTriggerVolumeChart };
